import React from 'react';
import { 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay, 
  isToday,
  format
} from 'date-fns';
import { 
  BookOpen, 
  Calendar as CalendarIcon, 
  Users, 
  CheckCircle, 
  AlertCircle 
} from 'lucide-react';

const CalendarGrid = ({ currentDate, events, onDateClick, onEventClick }) => {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const calendarDays = eachDayOfInterval({
    start: startDate,
    end: endDate
  });

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Helper to filter events for a specific day
  const getEventsForDay = (date) => {
    return events.filter(event => {
      // Use date string comparison to avoid time issues
      const eventStart = new Date(event.startDate).toDateString();
      const eventEnd = new Date(event.endDate).toDateString();
      const current = date.toDateString();
      return eventStart === current || eventEnd === current || 
             (date >= new Date(event.startDate) && date <= new Date(event.endDate));
    });
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'exam': return 'bg-red-100 text-red-700 border-red-200';
      case 'holiday': return 'bg-green-100 text-green-700 border-green-200';
      case 'event': return 'bg-[color-mix(in_srgb,var(--primary-color,#4f46e5)_10%,white)] text-[var(--primary-color,#4f46e5)] border-[color-mix(in_srgb,var(--primary-color,#4f46e5)_20%,white)]';
      case 'meeting': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'result_day': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden">
      {/* Week Header */}
      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {weekDays.map(day => (
          <div key={day} className="py-1.5 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider">
            {day}
          </div>
        ))}
      </div>

      {/* Days Grid */}
      <div className="grid grid-cols-7 auto-rows-fr bg-gray-200 gap-px border-b border-gray-200">
        {calendarDays.map((day, dayIdx) => {
          const dayEvents = getEventsForDay(day);
          const isCurrentMonth = isSameMonth(day, monthStart);
          const isCurrentDay = isToday(day);

          return (
            <div
              key={day.toString()}
              onClick={() => onDateClick(day)}
              className={`min-h-[80px] bg-white p-1 transition-all hover:bg-gray-50 cursor-pointer relative group ${
                !isCurrentMonth ? 'bg-gray-50/50' : ''
              }`}
            >
              <div className="flex items-center justify-between mb-1">
                <span 
                  className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full ${
                    isCurrentDay 
                      ? 'text-white' 
                      : !isCurrentMonth 
                        ? 'text-gray-400' 
                        : 'text-gray-700'
                  }`}
                  style={isCurrentDay ? { backgroundColor: 'var(--primary-color, #4f46e5)' } : {}}
                >
                  {format(day, 'd')}
                </span>
                {dayEvents.length > 0 && (
                  <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-1 py-0.5 rounded-full">
                    {dayEvents.length}
                  </span>
                )}
              </div>

              {/* Events Stack */}
              <div className="space-y-0.5 overflow-y-auto max-h-[60px] custom-scrollbar">
                {dayEvents.slice(0, 4).map(event => (
                  <div
                    key={event._id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(event);
                    }}
                    className={`text-[10px] px-1 py-0.5 rounded border truncate font-medium transition-transform hover:scale-[1.02] leading-tight ${getEventTypeColor(event.type)}`}
                    title={event.title}
                  >
                    {event.title}
                  </div>
                ))}
                {dayEvents.length > 4 && (
                  <div className="text-[10px] text-gray-500 font-medium pl-1">
                    +{dayEvents.length - 4} more
                  </div>
                )}
              </div>
              
              {/* Add hint on hover */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 pointer-events-none flex items-center justify-center bg-gray-50/10">
                 <Plus className="h-5 w-5" style={{ color: 'color-mix(in srgb, var(--primary-color, #4f46e5) 30%, transparent)' }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* Helper Import workaround for icon usage inside grid (if needed) */
// Note: Imports are at top level.

function Plus({ className }) {
   return (
     <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
       <line x1="12" y1="5" x2="12" y2="19"></line>
       <line x1="5" y1="12" x2="19" y2="12"></line>
     </svg>
   )
}

export default CalendarGrid;
